import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CartSummary from './components/CartSummary';
import TicketAssignment from './components/TicketAssignment';
import PaymentForm from './components/PaymentForm';
import UpsellSection from './components/UpsellSection';
import SecurityIndicators from './components/SecurityIndicators';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cartCollapsed, setCartCollapsed] = useState(true);
  const [errors, setErrors] = useState({});
  const [addedUpsells, setAddedUpsells] = useState(new Set());
  const [ticketAssignments, setTicketAssignments] = useState({});

  // Mock data
  const [cartItems, setCartItems] = useState([
    {
      id: 'ticket-1',
      name: 'General Admission',
      category: 'Ticket',
      details: 'Summer Music Festival 2024 - July 15, 2024',
      price: 89.99,
      quantity: 2,
      image: 'https://images.unsplash.com/photo-1493225457124-a3eb161ffa5f?w=400&h=300&fit=crop'
    },
    {
      id: 'upgrade-1',
      name: 'VIP Upgrade',
      category: 'Upgrade',
      details: 'Priority entry + VIP lounge access',
      price: 45.00,
      quantity: 1,
      image: 'https://images.unsplash.com/photo-1514525253161-7a46d19cd819?w=400&h=300&fit=crop'
    },
    {
      id: 'merch-1',
      name: 'Festival T-Shirt',
      category: 'Merchandise',
      details: 'Size: Large, Color: Black',
      price: 25.99,
      quantity: 1,
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?w=400&h=300&fit=crop'
    }
  ]);

  const ticketItems = cartItems?.filter(item => item?.category === 'Ticket');

  const upsellRecommendations = [
    {
      id: 'parking-1',
      name: 'Premium Parking',
      description: 'Reserved parking spot close to venue entrance',
      price: 15.00,
      originalPrice: 20.00,
      image: 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=400&h=300&fit=crop',
      features: ['Reserved spot', 'Close to entrance', 'Security monitored']
    },
    {
      id: 'food-1',
      name: 'Food & Drink Package',
      description: '2 meals + 3 drinks included',
      price: 35.00,
      originalPrice: 45.00,
      image: 'https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=300&fit=crop',
      features: ['2 full meals', '3 beverages', 'Skip food lines']
    },
    {
      id: 'photo-1',
      name: 'Professional Photos',
      description: 'Professional event photography package',
      price: 25.00,
      image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=400&h=300&fit=crop',
      features: ['High-res photos', 'Digital download', 'Social media ready']
    },
    {
      id: 'transport-1',
      name: 'Shuttle Service',
      description: 'Round-trip shuttle from downtown',
      price: 12.00,
      originalPrice: 18.00,
      image: 'https://images.unsplash.com/photo-1544620347-c4fd4a3d5957?w=400&h=300&fit=crop',
      features: ['Round-trip service', 'Air conditioned', 'Multiple pickup points']
    }
  ];

  const savedPaymentMethods = [
    {
      id: 'card-1',
      last4: '4242',
      brand: 'visa',
      expiryMonth: 12,
      expiryYear: 2025
    },
    {
      id: 'card-2',
      last4: '0005',
      brand: 'mastercard',
      expiryMonth: 8,
      expiryYear: 2026
    }
  ];

  const isAuthenticated = true; // Mock authentication state

  useEffect(() => {
    // Initialize ticket assignments for purchaser
    const initialAssignments = {};
    ticketItems?.forEach(ticket => {
      initialAssignments[ticket.id] = Array.from({ length: ticket?.quantity }, (_, index) => ({
        firstName: index === 0 ? 'John' : '',
        lastName: index === 0 ? 'Doe' : '',
        email: index === 0 ? 'john.doe@example.com' : '',
        phone: index === 0 ? '+1 (555) 123-4567' : ''
      }));
    });
    setTicketAssignments(initialAssignments);
  }, []);

  const handleUpdateQuantity = (itemId, newQuantity) => {
    if (newQuantity === 0) {
      handleRemoveItem(itemId);
      return;
    }

    setCartItems(prev => prev?.map(item => 
      item?.id === itemId ? { ...item, quantity: newQuantity } : item
    ));
  };

  const handleRemoveItem = (itemId) => {
    setCartItems(prev => prev?.filter(item => item?.id !== itemId));
    
    // Remove ticket assignments if it's a ticket
    if (ticketAssignments?.[itemId]) {
      setTicketAssignments(prev => {
        const updated = { ...prev };
        delete updated?.[itemId];
        return updated;
      });
    }
  };

  const handleAddUpsell = (upsellItem) => {
    const newItem = {
      id: upsellItem?.id,
      name: upsellItem?.name,
      category: 'Add-on',
      details: upsellItem?.description,
      price: upsellItem?.price,
      quantity: 1,
      image: upsellItem?.image
    };

    setCartItems(prev => [...prev, newItem]);
    setAddedUpsells(prev => new Set([...prev, upsellItem.id]));
  };

  const handleUpdateTicketAssignment = (ticketId, assignments) => {
    setTicketAssignments(prev => ({
      ...prev,
      [ticketId]: assignments
    }));
  };

  const validateForm = (paymentData) => {
    const newErrors = {};

    // Validate ticket assignments
    Object.entries(ticketAssignments)?.forEach(([ticketId, assignments]) => {
      assignments?.forEach((assignment, index) => {
        if (!assignment?.firstName?.trim()) {
          newErrors[`${ticketId}_${index}_firstName`] = 'First name is required';
        }
        if (!assignment?.lastName?.trim()) {
          newErrors[`${ticketId}_${index}_lastName`] = 'Last name is required';
        }
        if (!assignment?.email?.trim()) {
          newErrors[`${ticketId}_${index}_email`] = 'Email is required';
        } else if (!/\S+@\S+\.\S+/?.test(assignment?.email)) {
          newErrors[`${ticketId}_${index}_email`] = 'Invalid email format';
        }
      });
    });

    // Validate payment data
    if (!paymentData?.useExistingCard) {
      if (!paymentData?.cardholderName?.trim()) {
        newErrors.cardholderName = 'Cardholder name is required';
      }
      if (!paymentData?.cardNumber?.replace(/\s/g, '')) {
        newErrors.cardNumber = 'Card number is required';
      }
      if (!paymentData?.expiryDate) {
        newErrors.expiryDate = 'Expiry date is required';
      }
      if (!paymentData?.cvv) {
        newErrors.cvv = 'CVV is required';
      }
    } else if (!paymentData?.selectedCardId) {
      newErrors.selectedCardId = 'Please select a payment method';
    }

    // Validate billing address
    if (!paymentData?.billingAddress?.street?.trim()) {
      newErrors['billingAddress.street'] = 'Street address is required';
    }
    if (!paymentData?.billingAddress?.city?.trim()) {
      newErrors['billingAddress.city'] = 'City is required';
    }
    if (!paymentData?.billingAddress?.state) {
      newErrors['billingAddress.state'] = 'State is required';
    }
    if (!paymentData?.billingAddress?.zipCode?.trim()) {
      newErrors['billingAddress.zipCode'] = 'ZIP code is required';
    }

    return newErrors;
  };

  const handlePaymentSubmit = async (paymentData) => {
    const formErrors = validateForm(paymentData);
    
    if (Object.keys(formErrors)?.length > 0) {
      setErrors(formErrors);
      return;
    }

    setErrors({});
    setIsProcessing(true);

    try {
      // Simulate payment processing
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      // Mock successful payment
      const orderData = {
        orderId: 'ORD-' + Math.random()?.toString(36)?.substr(2, 9)?.toUpperCase(),
        items: cartItems,
        assignments: ticketAssignments,
        paymentMethod: paymentData?.useExistingCard ? 'saved' : 'new',
        total: cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) * 1.08 + 2.50
      };

      // Navigate to success page with order data
      navigate('/order-success', { state: { orderData } });
    } catch (error) {
      setErrors({ general: 'Payment failed. Please try again.' });
    } finally {
      setIsProcessing(false);
    }
  };

  const handleBackToEvent = () => {
    navigate('/event-detail');
  };

  if (cartItems?.length === 0) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-16">
            <Icon name="ShoppingCart" size={64} className="text-muted-foreground mx-auto mb-6" />
            <h1 className="text-2xl font-bold text-foreground mb-4">Your cart is empty</h1>
            <p className="text-muted-foreground mb-8">
              Add some tickets to get started with your purchase.
            </p>
            <Button
              variant="default"
              onClick={() => navigate('/home')}
              iconName="ArrowLeft"
              iconPosition="left"
            >
              Browse Events
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-elevation-1">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBackToEvent}
                iconName="ArrowLeft"
                iconPosition="left"
              >
                Back to Event
              </Button>
              <div className="hidden sm:block w-px h-6 bg-border"></div>
              <h1 className="text-xl font-semibold text-foreground">Checkout</h1>
            </div>
            
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Lock" size={16} className="text-success" />
              <span>Secure Checkout</span>
            </div>
          </div>
        </div>
      </div>
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Cart & Assignment */}
          <div className="lg:col-span-2 space-y-6">
            {/* Cart Summary */}
            <CartSummary
              cartItems={cartItems}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveItem}
              isCollapsed={cartCollapsed}
              onToggleCollapse={() => setCartCollapsed(!cartCollapsed)}
            />

            {/* Ticket Assignment */}
            {ticketItems?.length > 0 && (
              <TicketAssignment
                tickets={ticketItems}
                assignments={ticketAssignments}
                onUpdateAssignment={handleUpdateTicketAssignment}
                errors={errors}
              />
            )}

            {/* Upsell Section */}
            <UpsellSection
              recommendations={upsellRecommendations}
              onAddToCart={handleAddUpsell}
              addedItems={addedUpsells}
            />
          </div>

          {/* Right Column - Payment */}
          <div className="space-y-6">
            {/* Security Indicators */}
            <SecurityIndicators />

            {/* Payment Form */}
            <PaymentForm
              onSubmit={handlePaymentSubmit}
              isProcessing={isProcessing}
              savedPaymentMethods={savedPaymentMethods}
              isAuthenticated={isAuthenticated}
              errors={errors}
            />

            {/* Order Summary for Desktop */}
            <div className="hidden lg:block">
              <div className="bg-muted rounded-lg p-4">
                <h3 className="font-medium text-foreground mb-3">Order Total</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Subtotal</span>
                    <span className="text-foreground">
                      ${cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0)?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Tax</span>
                    <span className="text-foreground">
                      ${(cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) * 0.08)?.toFixed(2)}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Processing Fee</span>
                    <span className="text-foreground">$2.50</span>
                  </div>
                  <div className="flex justify-between text-lg font-semibold pt-2 border-t border-border">
                    <span className="text-foreground">Total</span>
                    <span className="text-foreground">
                      ${(cartItems?.reduce((sum, item) => sum + (item?.price * item?.quantity), 0) * 1.08 + 2.50)?.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Error Display */}
        {errors?.general && (
          <div className="mt-6 bg-error/10 border border-error/20 rounded-lg p-4">
            <div className="flex items-center space-x-2">
              <Icon name="AlertTriangle" size={20} className="text-error" />
              <span className="text-error font-medium">{errors?.general}</span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CheckoutPage;